import React, { useState, useEffect } from 'react';
import { Award, RefreshCw, Plus } from 'lucide-react';
import { api } from '../api';
import { PrivateWorkshopRequestModal } from '../components/modals/PrivateWorkshopModal';

const statusConfig = {
  pending:  { cls: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  approved: { cls: 'bg-green-100 text-green-800',  label: 'Approved' },
  rejected: { cls: 'bg-red-100 text-red-800',      label: 'Rejected' },
};

const MyPrivateWorkshopRequestsPage = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await api.request('/private-workshop-requests');
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const statusBadge = (s) => {
    const cfg = statusConfig[s] || { cls: 'bg-gray-100 text-gray-700', label: s };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${cfg.cls}`}>
        {cfg.label}
      </span>
    );
  };

  return (
    <div className="space-y-4" data-testid="my-private-workshop-requests-page">
      <div className="bg-levelup-dark rounded-xl p-4 text-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-3">
            <Award size={26} className="text-levelup-accent" />
            My Private Delivery Requests
          </h1>
          <p className="text-gray-300 mt-1 text-sm">
            Track the status of private workshop requests submitted by your organisation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg text-sm font-semibold hover:bg-levelup-accent-hover transition-colors"
            data-testid="new-private-workshop-request-btn"
          >
            <Plus size={15} /> New Request
          </button>
          <button
            onClick={fetchRequests}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-colors"
            data-testid="refresh-private-workshop-requests"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin w-10 h-10 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto" />
          <p className="text-gray-400 mt-3 text-sm">Loading requests…</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <Award size={44} className="text-gray-200 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No private workshop requests yet</p>
          <p className="text-gray-400 text-sm mt-1">
            Click "New Request" to request a tailored, customised workshop delivery for your organisation.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Course / Topic', 'Attendance', 'Timeframe', 'Language', 'Status', 'Admin Notes', 'Submitted'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map(req => (
                <tr key={req.id} className="hover:bg-gray-50 transition-colors" data-testid={`my-private-workshop-row-${req.id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900 max-w-[220px]">
                    <span className="line-clamp-2" title={req.course_of_interest}>{req.course_of_interest}</span>
                  </td>
                  <td className="px-3 py-2 text-center text-gray-700">{req.expected_attendance}</td>
                  <td className="px-3 py-2 text-gray-600">{req.preferred_timeframe}</td>
                  <td className="px-3 py-2 text-gray-600">{req.preferred_language || '—'}</td>
                  <td className="px-3 py-2">{statusBadge(req.status)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs max-w-[180px]">
                    {req.admin_notes || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-xs">
                    {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
            {requests.length} request{requests.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {showModal && (
        <PrivateWorkshopRequestModal user={user} onClose={() => { setShowModal(false); fetchRequests(); }} />
      )}
    </div>
  );
};

export default MyPrivateWorkshopRequestsPage;
